/**
 * LoadBoundary — what the agent sees when an Events screen (or the scanner inside
 * one) fails to load or render, instead of React Router's developer page.
 *
 * The Events screens are lazy chunks: a deploy that replaced them while the app
 * was open, a dropped connection, or a missing module rejects the import, and
 * the error lands here. "Reload" fetches the current build. Keyed by the caller
 * on the route, so moving to another screen clears it.
 *
 *   <LoadBoundary>              full screen: "Couldn't load this page"
 *   <LoadBoundary inline …>     a box in place of one part (the camera scanner)
 */
import { Component } from 'react';
import { Button } from 'antd';

export default class LoadBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Keep it in the console for whoever is debugging; the agent gets the screen below.
    console.error('[events] screen failed to load', error);
  }

  render() {
    const { error } = this.state;
    const { inline, title, sub, children } = this.props;
    if (!error) return children;

    const reload = () => window.location.reload();
    if (inline) {
      return (
        <div role="alert" style={{ flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 20, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 14, background: 'var(--surface)' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title || 'The scanner couldn’t load'}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: 280, lineHeight: 1.5 }}>
            {sub || 'Reload and try again. You can also show your ticket to the staff at the door.'}
          </div>
          <Button onClick={reload}>Reload</Button>
        </div>
      );
    }
    return (
      <div role="alert" style={{ flex: 1, minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 28, textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 700 }}>{title || 'Couldn’t load this page'}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 300, lineHeight: 1.5 }}>
          {sub || 'Check your connection, then reload. If CIDAS has just updated, reloading picks up the new version.'}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button type="primary" onClick={reload}>Reload</Button>
          <Button onClick={() => window.location.assign('/events')}>Events</Button>
        </div>
      </div>
    );
  }
}
